import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Impact,
  ImpactSummary,
  type Team,
  useAccessRules,
  useCreateTeam,
  useDeleteTeam,
  UserSelect,
  useSetTeamLeader,
  useSetTeamMember,
  useTeams,
  useUpdateTeam,
  useWorkspaceMembers,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import {
  Avatar,
  Button,
  ConfirmDialog,
  ErrorState,
  Icons,
  LoadingState,
  TextField,
  useToast,
} from '@/shared/ui';

import styles from './Admin.module.css';
import { RulesPanel } from './RulesPanel';

/**
 * Teams: a compact searchable list (name, leader, participants, rules) and - for the selected
 * team only - an editor with its members, leader and rules. Access rules of the whole
 * workspace are a second view, so a long list of teams never renders every form at once.
 */
export function TeamsTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const teams = useTeams(workspace.id);
  const members = useWorkspaceMembers(workspace.id);
  const create = useCreateTeam(workspace.id);
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  const [view, setView] = useState<'teams' | 'rules'>('teams');
  const canTeams = can(workspace.permissions, PERMISSIONS.workspaceManageTeams);
  const canRules = can(workspace.permissions, PERMISSIONS.workspaceConfigureBoardAccess);
  const people = useMemo(() => members.data ?? [], [members.data]);

  if (teams.isPending) return <LoadingState />;
  if (teams.isError) return <ErrorState error={teams.error} />;
  const current = teams.data.find((team) => team.id === opened) ?? null;
  if (current) {
    return (
      <TeamEditor
        key={current.id}
        workspace={workspace}
        team={current}
        canTeams={canTeams}
        onBack={() => {
          setOpened(null);
        }}
      />
    );
  }
  const needle = search.trim().toLocaleLowerCase();
  const shown = teams.data.filter(
    (team) => needle === '' || team.name.toLocaleLowerCase().includes(needle),
  );
  const leaderName = (team: Team) =>
    people.find((p) => p.user_id === team.leader_user_id)?.display_name ??
    t('workspaceAdmin.noLeader');
  return (
    <div className={styles.panel}>
      {canRules && (
        <div className={styles.segmented} role="group" aria-label={t('workspaceAdmin.tabs.teams')}>
          {(['teams', 'rules'] as const).map((id) => (
            <button
              key={id}
              type="button"
              className={view === id ? styles.segmentOn : styles.segment}
              aria-pressed={view === id}
              onClick={() => {
                setView(id);
              }}
            >
              {t(`workspaceAdmin.teamsView.${id}`)}
            </button>
          ))}
        </div>
      )}
      {view === 'rules' && canRules ? (
        <RulesPanel workspace={workspace} />
      ) : (
        <>
          <div className={styles.toolbar}>
            <input
              type="search"
              className={styles.field}
              value={search}
              placeholder={t('workspaceAdmin.searchTeams')}
              aria-label={t('workspaceAdmin.searchTeams')}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </div>
          {canTeams && (
            <form
              className={styles.inlineForm}
              onSubmit={(event) => {
                event.preventDefault();
                const value = name.trim();
                if (!value) return;
                create.mutate(
                  { name: value },
                  {
                    onSuccess: () => {
                      setName('');
                    },
                  },
                );
              }}
            >
              <TextField
                label={t('workspaceAdmin.teamName')}
                value={name}
                maxLength={60}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
              <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
                <Icons.plus /> {t('workspaceAdmin.addTeam')}
              </Button>
            </form>
          )}
          <FormError error={create.error} />
          {teams.data.length === 0 && <p className={styles.note}>{t('workspaceAdmin.noTeams')}</p>}
          {teams.data.length > 0 && shown.length === 0 && (
            <p className={styles.note}>{t('common.noResults')}</p>
          )}
          <ul className={styles.teamList} aria-label={t('workspaceAdmin.tabs.teams')}>
            {shown.map((team) => (
              <li key={team.id} data-testid="team">
                <button
                  type="button"
                  className={styles.teamItem}
                  onClick={() => {
                    setOpened(team.id);
                  }}
                >
                  <span className={styles.teamName}>{team.name}</span>
                  <span className={styles.meta}>
                    {t('workspaceAdmin.teamLeader')}: {leaderName(team)}
                  </span>
                  <span className={styles.meta}>
                    {t('workspaceAdmin.teamMembers', { count: team.member_ids.length })} ·{' '}
                    {t('workspaceAdmin.teamRules', { count: team.rule_count })}
                  </span>
                  <Icons.arrowRight />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function TeamEditor({
  workspace,
  team,
  canTeams,
  onBack,
}: {
  workspace: Workspace;
  team: Team;
  canTeams: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const members = useWorkspaceMembers(workspace.id);
  const rules = useAccessRules(
    workspace.id,
    can(workspace.permissions, PERMISSIONS.workspaceConfigureBoardAccess),
  );
  const update = useUpdateTeam(workspace.id);
  const setMember = useSetTeamMember(workspace.id);
  const setLeader = useSetTeamLeader(workspace.id);
  const remove = useDeleteTeam(workspace.id);
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description ?? '');
  const [deleting, setDeleting] = useState<Impact | null>(null);
  const canLeaders = can(workspace.permissions, PERMISSIONS.workspaceManageTeamLeaders);
  const people = members.data ?? [];
  const dirty = name.trim() !== team.name || description !== (team.description ?? '');
  const teamRules = (rules.data ?? []).filter((rule) => rule.team_id === team.id);

  return (
    <div className={styles.panel}>
      <div>
        <Button size="sm" variant="ghost" onClick={onBack}>
          <Icons.chevronLeft /> {t('workspaceAdmin.backToTeams')}
        </Button>
      </div>
      <section className={styles.card} aria-label={team.name}>
        <div className={styles.cardHead}>
          <h3 className={styles.subheading}>{team.name}</h3>
          <span className={styles.meta}>
            {t('workspaceAdmin.teamMembers', { count: team.member_ids.length })} ·{' '}
            {t('workspaceAdmin.teamRules', { count: team.rule_count })}
          </span>
        </div>
        <TextField
          label={t('workspaceAdmin.teamName')}
          value={name}
          maxLength={60}
          disabled={!canTeams}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          label={t('workspaceAdmin.description')}
          value={description}
          maxLength={500}
          disabled={!canTeams}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        {canTeams && (
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={!dirty || !name.trim() || update.isPending}
              onClick={() => {
                update.mutate(
                  { teamId: team.id, name: name.trim(), description: description.trim() || null },
                  {
                    onSuccess: () => {
                      toast.show({ message: t('workspaceAdmin.saved') });
                    },
                  },
                );
              }}
            >
              {t('common.save')}
            </Button>
          </div>
        )}

        <div className={styles.leaderRow}>
          <span className={styles.fieldLabel}>{t('workspaceAdmin.teamLeader')}</span>
          {canLeaders ? (
            <div className={styles.leaderPick}>
              <UserSelect
                label={t('workspaceAdmin.teamLeaderOf', { name: team.name })}
                users={people.filter((p) => team.member_ids.includes(p.user_id))}
                value={team.leader_user_id}
                placeholder={t('workspaceAdmin.noLeader')}
                disabled={setLeader.isPending}
                onChange={(userId) => {
                  setLeader.mutate({ teamId: team.id, userId });
                }}
              />
              {team.leader_user_id !== null && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={setLeader.isPending}
                  onClick={() => {
                    setLeader.mutate({ teamId: team.id, userId: null });
                  }}
                >
                  {t('workspaceAdmin.clearLeader')}
                </Button>
              )}
            </div>
          ) : (
            <span>
              {people.find((p) => p.user_id === team.leader_user_id)?.display_name ??
                t('workspaceAdmin.noLeader')}
            </span>
          )}
        </div>

        <h4 className={styles.subheading}>{t('workspaceAdmin.teamParticipants')}</h4>
        <ul className={styles.list}>
          {team.member_ids.map((id) => {
            const person = people.find((p) => p.user_id === id);
            return (
              <li key={id} className={styles.row}>
                <Avatar id={id} name={person?.display_name ?? '…'} size="sm" />
                <span className={styles.grow}>
                  {person?.display_name ?? '…'}
                  {team.leader_user_id === id && (
                    <span className={styles.chip}>{t('workspaceAdmin.leaderBadge')}</span>
                  )}
                </span>
                {canTeams && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={setMember.isPending}
                    onClick={() => {
                      setMember.mutate({ teamId: team.id, userId: id, present: false });
                    }}
                  >
                    {t('workspaceAdmin.removeFromTeam')}
                  </Button>
                )}
              </li>
            );
          })}
          {team.member_ids.length === 0 && (
            <li className={styles.note}>{t('workspaceAdmin.teamEmpty')}</li>
          )}
        </ul>
        {canTeams && (
          <div className={styles.field2}>
            <UserSelect
              label={t('workspaceAdmin.addToTeam')}
              users={people}
              value={null}
              placeholder={t('workspaceAdmin.addToTeam')}
              exclude={new Set(team.member_ids)}
              disabled={setMember.isPending}
              onChange={(userId) => {
                setMember.mutate({ teamId: team.id, userId, present: true });
              }}
            />
          </div>
        )}

        {teamRules.length > 0 && (
          <>
            <h4 className={styles.subheading}>{t('workspaceAdmin.teamRulesTitle')}</h4>
            <ul className={styles.list}>
              {teamRules.map((rule) => (
                <li key={rule.id} className={styles.row}>
                  <span className={styles.grow}>
                    {t('workspaceAdmin.ruleText', {
                      subject: team.name,
                      role: t(`roles.${rule.granted_role}`),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <FormError error={update.error ?? setMember.error ?? setLeader.error ?? remove.error} />
        {canTeams && (
          <div className={styles.actions}>
            <Button
              variant="danger"
              onClick={() => {
                remove.mutate(
                  { teamId: team.id, dryRun: true },
                  {
                    onSuccess: (impact) => {
                      setDeleting(impact);
                    },
                  },
                );
              }}
            >
              {t('workspaceAdmin.deleteTeam')}
            </Button>
          </div>
        )}
      </section>
      {deleting && (
        <>
          <ConfirmDialog
            title={t('workspaceAdmin.deleteTeamTitle', { name: team.name })}
            message={t('workspaceAdmin.deleteTeamMessage')}
            confirmLabel={t('common.delete')}
            pending={remove.isPending}
            onCancel={() => {
              setDeleting(null);
            }}
            onConfirm={() => {
              remove.mutate(
                { teamId: team.id },
                {
                  onSuccess: onBack,
                  onSettled: () => {
                    setDeleting(null);
                  },
                },
              );
            }}
          />
          <ImpactSummary impact={deleting} />
        </>
      )}
    </div>
  );
}
